import { useState, useRef, useEffect } from 'react'
import type { Card } from '../types/card'
import { generateMnemonic, type MnemonicResult } from '../services/deepseek'
import { playAudio } from '../utils/audio'
import HandwriteBoard from './HandwriteBoard'

interface Props {
  card: Card | null
  flipped: boolean
  onFlip: () => void
  onRate: (rating: 'forgot' | 'hard' | 'easy') => void
}

export default function FlashCard({ card, flipped, onFlip, onRate }: Props) {
  // ── 所有 Hook 无条件置顶，确保渲染次数一致 ──
  const [aiLoading, setAiLoading] = useState(false)
  const [aiMnemonic, setAiMnemonic] = useState<MnemonicResult | null>(null)
  const [aiError, setAiError] = useState<string | null>(null)
  const animKey = useRef(card?.id ?? 'empty')
  const [prevId, setPrevId] = useState(card?.id ?? '')

  // 切换卡片时重置状态并更新动画 key（副作用放 useEffect 而非 render 体内）
  useEffect(() => {
    const cid = card?.id ?? ''
    if (cid !== prevId) {
      setPrevId(cid)
      setAiMnemonic(null)
      setAiError(null)
      setAiLoading(false)
      animKey.current = cid
    }
  }, [card?.id, prevId])

  // ── 早期返回必须在所有 Hook 之后 ──
  if (!card) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 text-center px-5 py-16">
        <div className="text-6xl">🎉</div>
        <h2 className="text-xl font-bold text-white">
          太棒了！当前的复习任务已全部搞定！
        </h2>
        <p className="text-sm text-zinc-400 max-w-xs leading-relaxed">
          休息一下，或者切换到其他模式继续特训吧
        </p>
      </div>
    )
  }

  const handleSpeak = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (card?.front) playAudio(card.front)
  }

  const handleAI = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!card?.front) return
    setAiLoading(true)
    setAiError(null)
    setAiMnemonic(null)
    try {
      const result = await generateMnemonic({
        front: card.front,
        reading: card.back?.kanji || card.back?.romaji || '',
      })
      setAiMnemonic(result)
    } catch {
      setAiError('AI 请求失败，请检查网络后重试')
    } finally {
      setAiLoading(false)
    }
  }

  return (
    <div className="flex flex-col items-center gap-4 w-full max-w-md mx-auto px-4 sm:px-5">
      {/* 卡片 3D 翻转 */}
      <div
        className="relative w-full aspect-[4/3] cursor-pointer select-none perspective-1000"
        style={{ touchAction: 'manipulation' }}
        onClick={onFlip}
      >
        <div
          key={animKey.current}
          className="animate-card-in relative w-full h-full preserve-3d card-transition"
          style={{ transform: flipped ? 'rotateY(180deg)' : undefined }}
        >
          {/* 正面 */}
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-white/5 shadow-2xl backface-hidden">
            <span className="text-5xl sm:text-6xl font-bold text-white tracking-wider">
              {card?.front}
            </span>
            <span className="mt-4 text-xs text-slate-500">点击翻转 · Space</span>

            <button
              onClick={handleSpeak}
              className="absolute top-3 right-3 w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/15 text-sm transition-all duration-150 active:scale-90"
              title="播放发音"
            >
              🔊
            </button>
          </div>

          {/* 背面 */}
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 border border-white/5 shadow-2xl backface-hidden rotate-y-180 p-4 gap-2">
            <span className="text-4xl sm:text-5xl font-bold text-white tracking-wider">
              {card?.front}
            </span>
            <span className="text-xs text-zinc-500 font-mono tracking-wider">
              [{card?.back?.kanji || card?.back?.romaji || '?'}]
            </span>
            {card?.back?.meaning && (
              <span className="text-2xl sm:text-3xl font-semibold text-white">
                {card?.back?.meaning}
              </span>
            )}

            <button
              onClick={handleSpeak}
              className="w-8 h-8 sm:w-7 sm:h-7 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/15 text-xs transition-all duration-150 active:scale-90"
              title="播放发音"
            >
              🔊
            </button>

            {/* AI 联想记忆 */}
            <div className="mt-2 w-full flex flex-col items-center gap-2">
              {!aiMnemonic && !aiLoading && !aiError && (
                <button
                  onClick={handleAI}
                  className="px-4 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/80 text-xs transition-all duration-150 active:scale-95"
                >
                  ✨ AI 联想记忆
                </button>
              )}

              {aiLoading && (
                <div className="flex items-center gap-2 text-white/50 text-xs">
                  <span className="animate-spin inline-block w-3.5 h-3.5 border-2 border-white/20 border-t-white/60 rounded-full" />
                  AI 正在联想...
                </div>
              )}

              {aiError && (
                <p className="text-red-400/80 text-xs text-center leading-relaxed">
                  {aiError}
                </p>
              )}

              {aiMnemonic && (
                <div className="relative mt-1 px-4 pt-3 pb-3 rounded-2xl bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 border border-violet-400/30 max-w-[90%]">
                  <p className="text-base font-bold text-center text-white/90 leading-relaxed pr-7">
                    {aiMnemonic.jp}
                  </p>
                  <p className="text-xs text-center text-zinc-400 mt-1.5">
                    {aiMnemonic.cn}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      if (aiMnemonic?.jp) {
                        console.log('正在朗读AI例句:', aiMnemonic.jp)
                        playAudio(aiMnemonic.jp)
                      }
                    }}
                    className="absolute top-3 right-3 w-8 h-8 sm:w-7 sm:h-7 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/15 text-xs transition-all duration-150 active:scale-90"
                    title="播放日语短句"
                  >
                    🔊
                  </button>
                  <div className="absolute -top-1.5 left-4 w-3 h-3 rotate-45 bg-violet-500/20 border-l border-t border-violet-400/30 rounded-tl-sm" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 手写练习板（仅正面时显示） */}
      {!flipped && <HandwriteBoard />}

      {/* 操作按钮 */}
      <div className="flex gap-3 w-full">
        <button
          onClick={(e) => { e.stopPropagation(); setAiMnemonic(null); onRate('forgot') }}
          className="flex-1 py-4 sm:py-3.5 rounded-xl bg-red-600/90 hover:bg-red-500 active:scale-95 text-white font-semibold text-sm sm:text-sm transition-all duration-150 min-h-[48px]"
        >
          忘记<kbd className="ml-1.5 hidden sm:inline text-white/40 font-normal text-[10px]">1</kbd>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setAiMnemonic(null); onRate('hard') }}
          className="flex-1 py-4 sm:py-3.5 rounded-xl bg-amber-500/90 hover:bg-amber-400 active:scale-95 text-white font-semibold text-sm sm:text-sm transition-all duration-150 min-h-[48px]"
        >
          模糊<kbd className="ml-1.5 hidden sm:inline text-white/40 font-normal text-[10px]">2</kbd>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setAiMnemonic(null); onRate('easy') }}
          className="flex-1 py-4 sm:py-3.5 rounded-xl bg-emerald-600/90 hover:bg-emerald-500 active:scale-95 text-white font-semibold text-sm sm:text-sm transition-all duration-150 min-h-[48px]"
        >
          记得<kbd className="ml-1.5 hidden sm:inline text-white/40 font-normal text-[10px]">3</kbd>
        </button>
      </div>
    </div>
  )
}
